import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Phase 3 — Unified detail route.
 *
 * `/booked/:tripId` used to render `BookedTripDetailPage`; it now
 * redirects to the new unified `/chapters/:year/trip/:id` route so
 * existing bookmarks / affiliate-return links / mobile deep links keep
 * working. We fetch the trip to derive the year (from `start_date`),
 * then `navigate(..., { replace: true })` so the back button skips the
 * redirect step. Failure paths fall back to `/chapters/{currentYear}`
 * — the ChapterYearPage will 404-toast + navigate on its own if the
 * trip really doesn't exist.
 */
export default function BookedTripRedirect() {
  const { tripId } = useParams();
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fallbackYear = new Date().getFullYear();
      try {
        const { data } = await api.get(`/booked-trips/${tripId}`);
        if (cancelled) return;
        const y = parseInt((data?.start_date || "").slice(0, 4), 10) || fallbackYear;
        nav(`/chapters/${y}/trip/${tripId}`, { replace: true });
      } catch {
        if (cancelled) return;
        nav(`/chapters/${fallbackYear}`, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [tripId, nav]);

  return (
    <div
      data-testid="booked-trip-redirect"
      className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep text-tb-gold-bright font-hand text-xl"
    >
      ~ opening your trip ~
    </div>
  );
}
